import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { adminApi, NdaRecord } from '../services/api';

const NdasPage: React.FC = () => {
  const [ndas, setNdas] = useState<NdaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Generation state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchNdas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.listNdas();
      if (response.success) {
        setNdas(response.ndas || []);
      } else {
        setError(response.error?.message || 'Failed to fetch NDAs');
      }
    } catch (err: any) {
      // Only show error if it's not a 404 or empty response
      const errorMessage = err.response?.data?.error?.message || err.message || 'An unexpected error occurred';
      console.error('Error fetching NDAs:', err);
      setError(errorMessage);
      setNdas([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNdas();
  }, [fetchNdas]);

  const handleGenerate = async () => {
    if (!recipientName || !recipientAddress || !startDate) {
      setGenError('All fields are required');
      return;
    }

    setGenerating(true);
    setGenError(null);
    try {
      const response = await adminApi.generateNda({
        recipientName,
        recipientAddress,
        startDate,
      });

      if (response.success) {
        setGenerateOpen(false);
        setRecipientName('');
        setRecipientAddress('');
        setStartDate(new Date().toISOString().split('T')[0]);
        
        // Trigger automatic download
        if (response.downloadUrl) {
          window.open(response.downloadUrl, '_blank');
        }
        
        // Refresh the list
        fetchNdas();
      } else {
        setGenError(response.error?.message || 'Failed to generate NDA');
      }
    } catch (err: any) {
      setGenError(err.response?.data?.error?.message || err.message || 'An unexpected error occurred');
    } finally {
      setGenerating(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          NDA Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchNdas}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate NDA
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Recipient Name</TableCell>
                  <TableCell>Generated At</TableCell>
                  <TableCell>File Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : ndas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No NDAs found
                    </TableCell>
                  </TableRow>
                ) : (
                  ndas.map((nda) => (
                    <TableRow key={nda.filename}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          {nda.recipientName}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(nda.generatedAt)}</TableCell>
                      <TableCell>{formatFileSize(nda.fileSize)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download NDA">
                          <IconButton 
                            onClick={() => window.open(nda.url, '_blank')}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Generate NDA Dialog */}
      <Dialog open={generateOpen} onClose={() => !generating && setGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New NDA</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {genError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {genError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Recipient Name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              margin="normal"
              disabled={generating}
              placeholder="e.g. John Doe / ACME Corp"
            />
            <TextField
              fullWidth
              label="Recipient Address"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={generating}
              placeholder="Full physical address"
            />
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              margin="normal"
              disabled={generating}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={generating}
            startIcon={generating && <CircularProgress size={20} color="inherit" />}
          >
            {generating ? 'Generating...' : 'Generate & Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NdasPage;
